/**
 * Snacklins — buy-form behaviour.
 *
 * <snk-buy-form> wraps (does not replace) Horizon's <product-form-component>. It
 * scopes all state to itself so multiple instances can coexist on one page (the
 * Subscribe & Save landing renders three). It handles variant pill selection,
 * live price recompute (one-time + subscription first-order + recurring), and the
 * subscribe/one-time offer toggle (enable/disable the selling_plan input). The
 * actual add-to-cart + cart-drawer open is Horizon's product-form-component.
 *
 * <snk-flavor-switcher> toggles the visible product panel + gallery on the
 * Subscribe & Save landing page.
 *
 * Money is formatted naively as USD ($x.xx) — the store is USD-only (Grow plan,
 * no multi-currency Markets). If currency switching is ever added, swap in
 * @theme/money-formatting with the shop money format threaded through.
 */

/** @param {number} cents */
function money(cents) {
  return '$' + (cents / 100).toFixed(2);
}

class SnkBuyForm extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-snk-variants]');
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent || '[]');
    } catch (error) {
      console.warn('[snk-buy-form] Could not parse variants JSON', error);
      return;
    }

    this.firstPct = Number(this.dataset.firstOrderPct) || 30;
    this.knownSubscriber = this.dataset.knownSubscriber === 'true';

    this.idInput = this.querySelector('input[name="id"]');
    this.planInput = this.querySelector('[data-snk-plan-input]');
    this.frequency = this.querySelector('[data-snk-frequency]');
    this.frequencyWrap = this.querySelector('[data-snk-frequency-wrap]');
    this.cadenceEl = this.querySelector('[data-snk-cadence]');
    this.onceEl = this.querySelector('[data-snk-once-price]');
    this.subPriceEl = this.querySelector('[data-snk-sub-price]');
    this.subWasEl = this.querySelector('[data-snk-sub-was]');
    this.subRecurringEl = this.querySelector('[data-snk-sub-recurring]');
    this.addBtn = this.querySelector('[ref="addToCartButton"]');

    this.addEventListener('change', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.matches('[data-snk-pill]')) this.#update(Number(target.dataset.snkOptionIndex));
      if (target.matches('[data-snk-offer]')) this.#applyOffer();
      if (target.matches('[data-snk-frequency]')) this.#update();
    });

    this.#applyOffer();
    this.#update();
  }

  #selectedOptions() {
    const picks = [];
    this.querySelectorAll('[data-snk-pill]:checked').forEach((el) => {
      picks[Number(/** @type {HTMLElement} */ (el).dataset.snkOptionIndex)] = /** @type {HTMLInputElement} */ (el).value;
    });
    return picks;
  }

  /** Check the radio for a given option index + value (used to auto-snap away from an invalid combo). */
  #checkPill(optionIndex, value) {
    this.querySelectorAll(`[data-snk-pill][data-snk-option-index="${optionIndex}"]`).forEach((el) => {
      /** @type {HTMLInputElement} */ (el).checked = el.value === value;
    });
  }

  /**
   * Disable any option value that can't form a real variant with the OTHER options' current picks.
   * With the sparse Size×Pack matrix (0.9 oz has no 6-Pack), this greys out the impossible pill
   * instead of letting the user select a combo that maps to no variant.
   */
  #syncPillAvailability(picks) {
    this.querySelectorAll('[data-snk-pill]').forEach((el) => {
      const idx = Number(/** @type {HTMLElement} */ (el).dataset.snkOptionIndex);
      const val = /** @type {HTMLInputElement} */ (el).value;
      const ok = this.variants.some(
        (v) =>
          v.available &&
          v.options[idx] === val &&
          v.options.every((opt, i) => i === idx || picks[i] === undefined || opt === picks[i])
      );
      /** @type {HTMLInputElement} */ (el).disabled = !ok;
    });
  }

  /** @param {number} [changedIndex] Option index the shopper just clicked (for auto-snap). */
  #update(changedIndex) {
    let picks = this.#selectedOptions();
    let match = this.variants.find((v) => v.options.every((opt, i) => picks[i] === undefined || opt === picks[i]));

    // No exact match — the picked combo doesn't exist (e.g. 0.9 oz + 6-Pack). Keep the option the
    // shopper just changed and snap the OTHER option(s) to the first value that forms a real variant.
    if (!match && changedIndex !== undefined) {
      const keep = picks[changedIndex];
      const candidate =
        this.variants.find((v) => v.available && v.options[changedIndex] === keep) ||
        this.variants.find((v) => v.options[changedIndex] === keep);
      if (candidate) {
        candidate.options.forEach((val, i) => this.#checkPill(i, val));
        picks = this.#selectedOptions();
        match = candidate;
      }
    }

    this.#syncPillAvailability(picks);

    if (!match) return;

    if (this.idInput) this.idInput.value = match.id;

    const allocs = match.selling_plan_allocations || [];
    const planId = (a) => String(a.selling_plan_id ?? a.selling_plan?.id);
    if (this.frequency) {
      const options = Array.from(this.frequency.options);
      options.forEach((option) => { option.disabled = !allocs.some((a) => planId(a) === option.value); });
      if (!options.some((option) => option.selected && !option.disabled)) {
        const fallback = options.find((option) => !option.disabled && option.dataset.weeks === '4') || options.find((option) => !option.disabled);
        this.frequency.value = fallback ? fallback.value : '';
      }
    }
    const selectedPlan = this.frequency ? this.frequency.value : this.querySelector('[data-snk-offer][value="subscribe"]')?.dataset.snkSellingPlan;
    const alloc = allocs.find((a) => planId(a) === String(selectedPlan));
    const subscribe = this.querySelector('[data-snk-offer][value="subscribe"]');
    if (subscribe) {
      subscribe.disabled = !alloc;
      if (!alloc && subscribe.checked) {
        const once = this.querySelector('[data-snk-offer][value="onetime"]');
        if (once) once.checked = true;
      }
    }
    if (this.cadenceEl && this.frequency?.selectedOptions[0]) {
      this.cadenceEl.textContent = 'Deliver every ' + this.frequency.selectedOptions[0].dataset.weeks + ' weeks, 15% off';
    }
    this.#applyOffer();
    const firstOrder = Math.round((match.price * (100 - this.firstPct)) / 100);
    const recurring = alloc ? alloc.price : match.price;
    // Shopify rounds stacked cart discounts; display the exact recurring allocation when requested.
    const bigPrice = this.knownSubscriber || this.dataset.displayRecurringPrice === 'true' ? recurring : firstOrder;

    if (this.onceEl) this.onceEl.textContent = money(match.price);
    if (this.subPriceEl) this.subPriceEl.textContent = money(bigPrice);
    if (this.subWasEl) this.subWasEl.textContent = money(match.price);
    if (this.subRecurringEl && alloc) this.subRecurringEl.textContent = money(alloc.price);

    if (this.addBtn) {
      this.addBtn.disabled = !match.available;
      const textEl = this.addBtn.querySelector('.snk-buy__add-text') || this.addBtn;
      textEl.textContent = match.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  /** Submit the selling_plan only when "Subscribe" is chosen. */
  #applyOffer() {
    if (!this.planInput) return;
    const selected = this.querySelector('[data-snk-offer]:checked');
    const subscribing = selected && selected.value === 'subscribe' && !selected.disabled;
    const plan = subscribing ? (this.frequency ? this.frequency.value : selected.getAttribute('data-snk-selling-plan')) : '';
    if (this.frequencyWrap) this.frequencyWrap.hidden = !subscribing;
    if (this.frequency) this.frequency.disabled = !subscribing;
    if (plan) {
      this.planInput.value = plan;
      this.planInput.disabled = false;
    } else {
      this.planInput.value = '';
      this.planInput.disabled = true;
    }
  }
}

if (!customElements.get('snk-buy-form')) {
  customElements.define('snk-buy-form', SnkBuyForm);
}

class SnkFlavorSwitcher extends HTMLElement {
  connectedCallback() {
    this.tiles = Array.from(this.querySelectorAll('[data-snk-flavor]'));
    this.addEventListener('click', (event) => {
      const tile = /** @type {HTMLElement} */ (event.target).closest('[data-snk-flavor]');
      if (!tile) return;
      this.#select(tile.getAttribute('data-snk-flavor'));
    });
  }

  /** @param {string | null} productId */
  #select(productId) {
    if (!productId) return;

    this.tiles.forEach((tile) => {
      const active = tile.getAttribute('data-snk-flavor') === productId;
      tile.classList.toggle('is-active', active);
      tile.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.querySelectorAll('[data-snk-panel]').forEach((panel) => {
      panel.toggleAttribute('hidden', panel.getAttribute('data-snk-panel') !== productId);
    });
    this.querySelectorAll('[data-snk-panel-media]').forEach((media) => {
      media.toggleAttribute('hidden', media.getAttribute('data-snk-panel-media') !== productId);
    });
  }
}

if (!customElements.get('snk-flavor-switcher')) {
  customElements.define('snk-flavor-switcher', SnkFlavorSwitcher);
}
